import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import { Autocomplete,Button,Card,CardContent,InputAdornment,Pagination,Skeleton,Stack,TextField,Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useContext,useEffect,useState } from "react";
import { useTranslation } from "react-i18next";
import { IoMdArrowRoundForward } from 'react-icons/io';
import { toast } from "react-toastify";
import { UserContext } from "../../context/UserContext";
import AttToken from "../../helpers/attToken";
import { trackEventMatomo } from "../../helpers/matomo";
import PlanoDeAcao from "../../service/PlanoDeAcao";
import { planoDeAcaoType } from "../../types/plano-de-acao/types";
import EditaPlano from "./modal/EditaPlano";

const planosService = new PlanoDeAcao();

export default function MalSucedidos() {
  const { t, i18n } = useTranslation('translation');
  const { userData } = useContext(UserContext);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(5);
  const [paginated, setPaginated] = useState<planoDeAcaoType[]>([]);
  const [plano, setPlano] = useState<planoDeAcaoType | null>(null);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  function handleOpen() {
    setOpen(!open);
  }

  const lastPageIndex = currentPage * itemsPerPage;
  const firstPageIndex = lastPageIndex - itemsPerPage;

  const { data, isLoading, isFetching, isSuccess, isError } = useQuery({
    queryKey: ['GET_MALSUCEDIDOS'],
    queryFn: async () => {
      const token = await AttToken();
      if (token) {
        const result: planoDeAcaoType[] = await planosService.getRefuseds(token);
        return result;
      }
    },
    enabled: userData.listaFuncionalidades.includes('plano-de-acao'),
    retry: false,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (data?.length && isSuccess) {
      toast.success(`${data?.length} ${t('ActionPlan.ListaAnalises.failedActionPlans')}`, { toastId: 'successoPendente' });
    } else {
      toast.info(`${t('ActionPlan.ListaAnalises.noFailedActionPlans')}`, { toastId: 'successoPendente' });
    }
  }, [data, isSuccess, t]);

  useEffect(() => {
    if (isSuccess && !isFetching && !isLoading && data) {
      const currentPageData = data?.filter((ele) => ele.nome?.includes(search))
        ?.sort((a, b) => { return Number(a.dataCriacao) < Number(b.dataCriacao) ? 1 : -1; });
      setPaginated(currentPageData?.slice(firstPageIndex, lastPageIndex) ?? []);
    } if (isError) {
      toast.error('Erro ao buscar plaos malsucedidos', { toastId: 'erro' });
    }
  }, [firstPageIndex, lastPageIndex, data, isSuccess, isFetching, isLoading, isError, t, search]);

  const pages = [];

  if (data) {
    for (let i = 1; i <= Math.ceil(data?.filter((ele) => ele?.nome?.includes(search))?.length / itemsPerPage); i++) {
      pages.push(i);
    }
  }

  function Loading() {
    return (
      <>
        <Skeleton height={30} width='300px' variant="rounded" />
        {Array(5).fill(0).map((_, index) => (
          <Skeleton key={index} height={150} width='100%' variant="rounded" />
        ))}
      </>
    );
  };

  if (!data
    && !isLoading
    && !isFetching
  ) return (
    <span style={{
      width: '100%',
      display: 'flex',
      justifyContent: "center",
      color: 'gray',
      fontSize: '1.5em',
      fontWeight: 600
    }}>
      {t('ActionPlan.ListaAnalises.noAnalysesOrFailedPlans')}
    </span>);

  return (
    <>
      {open &&
        <EditaPlano
          handleOpen={handleOpen}
          openModal={open}
          plano={plano}
          setPlano={setPlano}
          restarting={true}
        />
      }
      <Stack
        direction='column'
        width='100%'
        justifyContent='flex-start'
        alignItems='flex-start'
        spacing={2}
      >
        {isLoading || isFetching
          ? <Loading />
          : data && <>
            <Stack
              justifyContent='space-between'
              alignItems='center'
              width='100%'
              direction="row"
              spacing={1}
            >
              {<Typography textAlign='left' variant='h6' sx={{ color: '#47AA9C' }}>
                {`${data?.length ? data.length : 'Nenhum'} ${data?.length > 1
                  ? `${t('ActionPlan.ListaAnalises.failedActionPlansLabel')}`
                  : `${t('ActionPlan.ListaAnalises.failedActionPlanLabel')}`}`}
              </Typography>}
              {data.length > 5
                && <Pagination
                  style={{ display: `${!pages?.length ? 'none' : ''}` }}
                  boundaryCount={4}
                  showFirstButton
                  showLastButton
                  shape="rounded"
                  page={currentPage}
                  count={pages?.length}
                  onChange={(eve, pageNumber) => {
                    trackEventMatomo('lista analise de PA', 'click', 'button', 'muda página');
                    setCurrentPage(pageNumber);
                  }}
                />}
            </Stack>
            {data?.length > 5
              && <Autocomplete
                fullWidth
                value={search ? ({ nome: search } as planoDeAcaoType) : null}
                getOptionLabel={option => option.nome as string}
                getOptionKey={option => option?.id as string}
                isOptionEqualToValue={(option, value) => option.nome === value.nome}
                options={data
                  ?.sort((a, b) => { return a.nome?.toLowerCase() > b.nome?.toLowerCase() ? 1 : - 1; })
                  ?.map((ele: planoDeAcaoType) => ({ ...ele })) || []}
                onChange={(_eve, value: planoDeAcaoType | null) => {
                  if (value?.nome) {
                    setSearch(value?.nome as string);
                    setCurrentPage(1);
                  }
                  else {
                    setSearch("");
                  }
                }}
                renderInput={(params) => <TextField {...params} label={t('ActionPlan.PlanosDeAcaoComp.searchPlanByName')} />}
              />}
            {paginated?.sort((a, b) => (b.dataCriacao || 0) - (a.dataCriacao || 0))?.map((plano: planoDeAcaoType, index: number) =>
              <Card key={index} sx={{ width: '100%' }}>
                <CardContent>
                  <Stack
                    width='100%'
                    direction='row'
                    justifyContent='space-between'
                    alignItems='flex-start'
                  >
                    <Typography variant='h5' fontWeight={600} sx={{ color: '#47AA9C' }}>
                      {plano.nome}
                    </Typography>
                    <Stack
                      direction='column'
                      justifyContent='flex-start'
                      alignItems='flex-end'
                      spacing={2}
                    >
                      <Button
                        variant='contained'
                        color='success'
                        endIcon={<IoMdArrowRoundForward />}
                        onClick={() => {
                          trackEventMatomo('lista analise de PA', 'click', 'button', 'reabre PA');
                          setPlano(plano);
                          setOpen(true);
                        }} >
                        {t('ActionPlan.ListaAnalises.reopen')}
                      </Button>
                      <TextField
                        inputProps={{
                          style: { textAlign: 'center' },
                        }}
                        slotProps={{
                          input: {
                            startAdornment: (
                              <InputAdornment position="start">
                                <CalendarMonthIcon />
                              </InputAdornment>
                            ),
                          },
                        }}
                        variant='outlined'
                        label={t('ActionPlan.ListaAnalises.formCompletionDate')}
                        value={new Date(plano.dataCriacao || 0).toLocaleString(i18n.language === 'en' ? 'en-US' : 'pt-BR')} disabled />
                      <TextField
                        inputProps={{
                          style: { textAlign: 'center' },
                        }}
                        slotProps={{
                          input: {
                            startAdornment: (
                              <InputAdornment position="start">
                                <CalendarMonthIcon />
                              </InputAdornment>
                            ),
                          },
                        }}
                        variant='outlined'
                        label={'Data de avaliação'}
                        value={new Date(plano.dataAvaliacao || 0).toLocaleString(i18n.language === 'en' ? 'en-US' : 'pt-BR')} disabled />
                    </Stack>
                  </Stack>
                </CardContent>
              </Card>
            )}
          </>}
      </Stack>
    </>
  );
}
